import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox'; // Assuming a Checkbox component exists
import { ShieldAlert, Mic, Info, ArrowLeft, Home } from 'lucide-react';

export const AudioTriageWelcome: React.FC = () => {
  const navigate = useNavigate();
  const [consentGiven, setConsentGiven] = useState(false);

  const handleProceed = () => {
    if (consentGiven) {
      // Navigate to the next step in the audio triage flow (symptom collection)
      navigate('/patient/audio-triage/symptoms'); 
    } else {
      // Optionally, show an alert or message that consent is required
      alert('Please provide consent to proceed with audio triage.');
    }
  };

  const handleGoBack = () => {
    navigate('/patient/help'); // Navigate back to the HelpOptionsScreen
  };

  const handleBackToDashboard = () => {
    navigate('/patient/dashboard'); // Navigate directly back to the patient dashboard
  };

  const handleViewPrivacyPolicy = () => {
    // Placeholder for viewing privacy policy - could open a modal or new tab
    alert('Display Privacy Policy Information Here');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 p-6 pt-12">
      <div className="w-full max-w-lg bg-white p-8 rounded-xl shadow-2xl">
        <div className="flex items-center justify-center mb-6">
          <Mic size={48} className="text-indigo-600 mr-3" />
          <h1 className="text-3xl font-bold text-slate-800 text-center">Audio Symptom Check</h1>
        </div>

        <p className="text-slate-600 mb-4 text-center">
          Welcome! This service allows you to describe your symptoms using your voice.
          It's a quick and easy way to help us understand how you're feeling.
        </p>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-indigo-700 mb-2 flex items-center">
            <ShieldAlert size={20} className="mr-2" /> How it Works & Consent
          </h2>
          <p className="text-sm text-slate-700 mb-3">
            We will guide you through a few questions. Your responses, including audio recordings of your symptoms, will be used to provide a preliminary assessment.
          </p>
          <div className="flex items-start space-x-3 mb-3">
            <Checkbox 
              id="consent"
              checked={consentGiven}
              onCheckedChange={(checkedState) => setConsentGiven(Boolean(checkedState))}
              className="mt-1 border-slate-400 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 focus-visible:ring-indigo-500"
              aria-describedby="consent-description"
            />
            <label htmlFor="consent" className="text-sm text-slate-700 cursor-pointer" id="consent-description">
              I understand and consent to my voice and symptom information being recorded and processed for this triage assessment.
            </label>
          </div>
          <Button variant="link" onClick={handleViewPrivacyPolicy} className="p-0 h-auto text-sm text-indigo-600 hover:text-indigo-700 hover:underline flex items-center">
            <Info size={16} className="mr-1" /> View Privacy Information
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Button 
            variant="outline"
            onClick={handleGoBack} 
            className="w-full sm:w-auto flex items-center justify-center border-slate-300 text-slate-700 hover:bg-slate-100 focus-visible:ring-indigo-500"
            aria-label="Go back to previous screen"
          >
            <ArrowLeft size={18} className="mr-2" /> Go Back
          </Button>
          <Button 
            variant="outline"
            onClick={handleBackToDashboard} 
            className="w-full sm:w-auto flex items-center justify-center border-slate-300 text-slate-700 hover:bg-slate-100 focus-visible:ring-indigo-500"
            aria-label="Return to dashboard"
          >
            <Home size={18} className="mr-2" /> Dashboard
          </Button>
          <Button 
            onClick={handleProceed} 
            disabled={!consentGiven}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed flex-grow focus-visible:ring-indigo-500"
            aria-label="Agree to terms and proceed with audio triage"
          >
            Agree & Proceed
          </Button>
        </div>
      </div>
       <p className="text-xs text-slate-500 mt-8 text-center max-w-md mx-auto">
        This is a preliminary assessment and not a substitute for professional medical advice. If this is an emergency, please call your local emergency number immediately.
      </p>
    </div>
  );
};
